import React, { Component, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ArgoContext, FrameworkStage, useArgoContext } from '@amzn/awsscm-argo-core';
import { credentialStorage } from '@amzn/awsscm-argo-core/argo/utils/CredentialStorage';
import { useApolloClient } from '@apollo/client';
import { isExternalArgoUser } from 'rfxportal/RFX/userPermissionHelper';

import Badge from '@amzn/awsui-components-react-v3/polaris/badge';
import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-react-v3/polaris/side-navigation';

import GetNotifications from '../../gql/GetNotifications';
import { DEVELOPER_PERMISSION_GROUPS_SET } from '../../interfaces';
import { Notification } from '../../interfaces/RFXBackendModel';
import urls from '../../resources/urls';
import userDisplayStrings from '../../resources/user_display_strings_rfx';
import {
  selectNotifications,
  selectUnreadNotificationBadge,
  setNotifications,
  setUnreadNotificationBadge
} from '../../rfxportal/reducers/BuyerViewPageSlice';
import store, { useAppDispatch } from '../../store';
import { doesListContainUserPermissionGroup } from '../../util/RFXUtils';

import './style.css';

const DEV_USER_NAV_ITEMS = (notificationCount?: number): SideNavigationProps.Item[] => [
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.rfq.buyerdashboard,
    href: urls.pages.smart + urls.pages.buyer + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.rfq.supplierdashboard,
    href: urls.pages.smart + urls.pages.supplier + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.changeOrder.dashboard,
    href: urls.pages.smart + urls.pages.changeorder + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.notifications,
    href: urls.pages.smart + urls.pages.notifications,
    info: notificationCount ? <Badge color="red">{notificationCount}</Badge> : undefined
  }
];

const BUYER_NAV_ITEMS = (notificationCount?: number): SideNavigationProps.Item[] => [
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.rfq.dashboard,
    href: urls.pages.smart + urls.pages.buyer + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.changeOrder.dashboard,
    href: urls.pages.smart + urls.pages.changeorder + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.notifications,
    href: urls.pages.smart + urls.pages.notifications,
    info: notificationCount ? <Badge color="red">{notificationCount}</Badge> : undefined
  }
];

const SUPPLIER_NAV_ITEMS = (): SideNavigationProps.Item[] => [
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.rfq.dashboard,
    href: urls.pages.smart + urls.pages.supplier + urls.pages.dashboard
  },
  {
    type: 'link',
    text: userDisplayStrings.leftNavigation.smart.changeOrder.dashboard,
    href: urls.pages.smart + urls.pages.changeorder + urls.pages.dashboard
  }
];

/**
 * @returns Returns navigation items depending on the argo context and credentials
 * of the user.
 */
const navItems = (argoContext: ArgoContext, notificationCount?: number): SideNavigationProps.Item[] => {
  const frameworkStage: FrameworkStage = argoContext.frameworkStage;
  const customGroups: string[] = credentialStorage.getCustomGroups();
  const isExternalUser: boolean = isExternalArgoUser();
  const isDevUser: boolean = doesListContainUserPermissionGroup(DEVELOPER_PERMISSION_GROUPS_SET, customGroups);

  if (frameworkStage === FrameworkStage.prod) {
    // For production stage if the user is not external, then show the buyer dashboard.
    // Otherwise, show the supplier dashboard.
    if (!isExternalUser) {
      return BUYER_NAV_ITEMS(notificationCount);
    } else {
      return SUPPLIER_NAV_ITEMS();
    }
  } else {
    // For non-production stage if the user is a developer, then show both dashboards.
    // Otherwise, for other internal users show the buyer dashboard. Lastly, for external
    // users show the supplier dashboard.
    if (isDevUser) {
      return DEV_USER_NAV_ITEMS(notificationCount);
    } else if (!isExternalUser) {
      return BUYER_NAV_ITEMS(notificationCount);
    } else {
      return SUPPLIER_NAV_ITEMS();
    }
  }
};

export class NavigationMenu extends Component {
  render() {
    return <NavigationContent />;
  }
}

export const NavigationContent = () => {
  const badgeData: number = useSelector(selectUnreadNotificationBadge);
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const selectorNotificationData = useSelector(selectNotifications);
  const [notificationData, setNotificationData] = React.useState(selectorNotificationData);

  const location = useLocation();
  const [activeHref, setActiveHref] = React.useState(urls.pages.smart + location.pathname);

  useEffect(() => {
    // query the GQL to get notifications
    async function getNotifications(startTime?: number, endTime?: number, providedToken?: string) {
      var response;
      if (!providedToken) {
        if (startTime && endTime) {
          response = await GetNotifications(client, startTime, endTime, undefined);
        } else {
          response = await GetNotifications(client, undefined, undefined, undefined);
        }
      } else {
        if (startTime && endTime) {
          response = await GetNotifications(client, startTime, endTime, providedToken);
        } else {
          response = await GetNotifications(client, undefined, undefined, providedToken);
        }
      }

      return response.notificationList;
    }

    // retrieve notifications if they have never been loaded before,
    // otherwise retrieve them every minute and update notification history if new events were found.
    if (notificationData.length === 0) {
      getNotifications()
        .then((result) => {
          const notificationList = result as Notification[];
          dispatch(setNotifications(notificationList));
          dispatch(setUnreadNotificationBadge(notificationList.filter((n) => n.messageState === 'UNREAD').length));
        })
        .catch((error) => console.log(error));
    } else {
      setInterval(() => {
        const endPollTime = Date.now();
        const startTime = endPollTime - 5 * 60 * 1000;
        getNotifications(startTime, endPollTime)
          .then((result) => {
            if (result && result.length > 0) {
              const oldNotificationIds = store.getState().buyerViewPage.notifications.map((n) => n.notificationId);
              const newNotifications = result.filter((n) => !oldNotificationIds.includes(n.notificationId));
              // append new result to set notifications
              dispatch(setNotifications([...newNotifications, ...store.getState().buyerViewPage.notifications]));
              dispatch(
                setUnreadNotificationBadge(
                  store.getState().buyerViewPage.notifications.filter((n) => n.messageState === 'UNREAD').length
                )
              );
            }
          })
          .catch((error) => console.log(error));
      }, 5 * 60 * 1000);
    }
  }, [client, dispatch, notificationData, notificationData.length]);

  const argoContext: ArgoContext = useArgoContext();
  const items: SideNavigationProps.Item[] = navItems(argoContext, badgeData);

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{ href: urls.pages.smart + urls.landing, text: userDisplayStrings.leftNavigation.smart.title }}
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          setActiveHref(event.detail.href);
          window.location.href = event.detail.href;
        }
      }}
      items={items}
    />
  );
};
